import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Dialog, DialogHeader } from "@astryxdesign/core/Dialog";
import { Layout, LayoutContent, LayoutFooter, HStack } from "@astryxdesign/core/Layout";
import { FormLayout } from "@astryxdesign/core/FormLayout";
import { RadioList, RadioListItem } from "@astryxdesign/core/RadioList";
import { Button } from "@astryxdesign/core/Button";
import { Banner } from "@astryxdesign/core/Banner";
import { type CameraScope, type User, api, isUnrestrictedScope, unrestrictedScope } from "../../lib/api";
import { CameraAccessEditor } from "../access/CameraAccessEditor";

type Mode = "inherit" | "custom";

interface FormValues {
  mode: Mode;
  isRestricted: boolean;
  cameraScope: CameraScope;
}

function valuesFor(user: User | null): FormValues {
  if (!user?.camera_scope_override) {
    return { mode: "inherit", isRestricted: false, cameraScope: unrestrictedScope };
  }
  return {
    mode: "custom",
    isRestricted: !isUnrestrictedScope(user.camera_scope_override),
    cameraScope: user.camera_scope_override,
  };
}

interface UserCameraAccessDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  /** null while no user is targeted — the dialog stays closed in that case. */
  target: User | null;
  isSaving: boolean;
  onSave: (id: string, cameraScope: CameraScope | null, version: number) => Promise<unknown>;
}

// The DMS-style per-account camera restriction override. Unlike
// RoleFormDialog's CameraAccessEditor (which always has a concrete scope),
// a user's override can also simply be absent — "inherit whatever the role
// grants" — so this dialog has one more state on top of the editor's own
// unrestricted/restricted toggle.
export function UserCameraAccessDialog({ isOpen, onOpenChange, target, isSaving, onSave }: UserCameraAccessDialogProps) {
  const [values, setValues] = useState<FormValues>(() => valuesFor(target));
  const [scopeFieldError, setScopeFieldError] = useState<string | null>(null);
  const [requestError, setRequestError] = useState<string | null>(null);

  const { data: projects } = useQuery({ queryKey: ["projects"], queryFn: api.projects.list, enabled: isOpen });
  const { data: cameras } = useQuery({ queryKey: ["cameras"], queryFn: api.cameras.list, enabled: isOpen });

  useEffect(() => {
    if (!isOpen) return;
    setScopeFieldError(null);
    setRequestError(null);
    setValues(valuesFor(target));
  }, [isOpen, target]);

  const handleSubmit = async () => {
    if (!target) return;
    setRequestError(null);

    if (values.mode === "custom" && values.isRestricted && isUnrestrictedScope(values.cameraScope)) {
      setScopeFieldError("Select at least one project or camera, or turn off camera restriction.");
      return;
    }
    setScopeFieldError(null);

    const cameraScope: CameraScope | null =
      values.mode === "inherit" ? null : values.isRestricted ? values.cameraScope : unrestrictedScope;

    try {
      await onSave(target.id, cameraScope, target.version);
      onOpenChange(false);
    } catch (err) {
      setRequestError(err instanceof Error ? err.message : "Something went wrong.");
    }
  };

  return (
    <Dialog isOpen={isOpen} onOpenChange={onOpenChange} purpose="form" width={560}>
      <Layout
        header={
          <DialogHeader
            title={target ? `Camera access for ${target.display_name || target.username}` : "Camera access"}
            subtitle="Overrides this account's role-level camera access. Doesn't affect anyone else with the same role."
            onOpenChange={onOpenChange}
          />
        }
        content={
          <LayoutContent>
            <FormLayout>
              <RadioList
                label="Source"
                isLabelHidden
                value={values.mode}
                onChange={(mode) => setValues((prev) => ({ ...prev, mode: mode as Mode }))}
              >
                <RadioListItem
                  value="inherit"
                  label="Inherit from role"
                  description={`Use whatever ${target?.role.name ?? "the account's role"} grants. Changes automatically if the role changes.`}
                />
                <RadioListItem
                  value="custom"
                  label="Custom for this account"
                  description="Set camera/project access specifically for this person, regardless of their role."
                />
              </RadioList>

              {values.mode === "custom" && (
                <CameraAccessEditor
                  isRestricted={values.isRestricted}
                  onRestrictedChange={(isRestricted) => setValues((prev) => ({ ...prev, isRestricted }))}
                  scope={values.cameraScope}
                  onScopeChange={(cameraScope) => setValues((prev) => ({ ...prev, cameraScope }))}
                  projects={projects ?? []}
                  cameras={cameras ?? []}
                  status={scopeFieldError ? { type: "error", message: scopeFieldError } : undefined}
                />
              )}

              {requestError && <Banner status="error" title="Couldn't save camera access" description={requestError} />}
            </FormLayout>
          </LayoutContent>
        }
        footer={
          <LayoutFooter>
            <HStack gap={2} hAlign="end">
              <Button label="Cancel" variant="ghost" onClick={() => onOpenChange(false)} isDisabled={isSaving} />
              <Button label="Save changes" variant="primary" clickAction={handleSubmit} isLoading={isSaving} />
            </HStack>
          </LayoutFooter>
        }
      />
    </Dialog>
  );
}
